import { Page, Header, HeaderLabel, Content } from '@backstage/core-components';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import { ArrowForwardIcon } from '../icons/ArrowForwardIcon';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type DevPlatformCardProps = {
  name: string;
  description: string;
  to: string;
  external?: boolean;
};

const DevPlatformCard = ({
  name,
  description,
  to,
  external,
}: DevPlatformCardProps) => {
  const isExternal = external ?? to.startsWith('http');

  const footerContent = (
    <>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1,
          mr: 0.5,
        }}
      >
        READ MORE
      </Typography>
      {isExternal ? (
        <OpenInNewIcon sx={{ width: 16, height: 16 }} />
      ) : (
        <Box sx={{ width: 16, height: 16, display: 'flex' }}>
          <ArrowForwardIcon />
        </Box>
      )}
    </>
  );

  return (
    <Card
      sx={{
        borderRadius: 1,
        boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.20)',
        display: 'grid',
        gridTemplateRows: 'subgrid',
        gridRow: 'span 4',
      }}
    >
      <Box
        sx={{
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          background: 'linear-gradient(90deg, #008294 0%, #66B8C4 100%)',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.5,
            fontFamily: '"Helvetica Neue", Roboto, sans-serif',
            color: '#FFFFFF',
            margin: 0,
          }}
        >
          {name}
        </Typography>
      </Box>

      <CardContent sx={{ padding: 2 }}>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </CardContent>

      <Divider />

      {isExternal ? (
        <Box
          component="a"
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            padding: '16px 24px 16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {footerContent}
        </Box>
      ) : (
        <Box
          component={RouterLink}
          to={to}
          sx={{
            padding: '6px 24px 18px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          {footerContent}
        </Box>
      )}
    </Card>
  );
};

export const DeveloperPlatformPage = () => {
  return (
    <Page themeId="plugin">
      <Header
        title="Developer Platform"
        subtitle="Your starting point for automated and scalable software development."
      >
        <HeaderLabel
          label="Owner"
          value="Core Portal"
          url="/catalog/default/group/core-portal"
        />
      </Header>

      <Content stretch noPadding>
        <Box
          sx={{
            minWidth: '100%',
            minHeight: '100vh',
            boxSizing: 'border-box',
            bgcolor: theme =>
              theme.palette.mode === 'light' ? '#F9F7F2' : 'background.default',
            p: 4,
            pb: 5,
          }}
        >
          <Box
            sx={{
              width: 1368,
              maxWidth: '100%',
            }}
          >
            <Box sx={{ maxWidth: 640, mb: 4 }}>
              <Typography variant="h4" gutterBottom>
                Developer Platform
              </Typography>

              <Typography variant="body1" paragraph>
                The Developer Platform gives you the fundamental building blocks
                for software development across Traton. You get pipelines,
                documentation, observability, cloud deployment and access
                management, providing everything you need to automate workflows,
                collaborate seamlessly and deliver secure, high-quality software
                with minimal setup and friction.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 2,
                gridAutoRows: 'auto auto 1fr auto',
              }}
            >
              <DevPlatformCard
                name="Agentic workflows"
                description="Guidelines, best practices, and golden path documentation for implementing and managing agentic workflows within your organization."
                to="/docs/default/component/traton-agentic-workflows"
                external={false}
              />
              <DevPlatformCard
                name="CoLa Access Management System (AMS)"
                description="AMS provides secure, scalable access management for Traton Group and strategic partners, enabling consistent user experience across cloud applications, supporting both user and service-to-service authentication flows."
                to="/docs/default/component/access-management-service"
                external
              />
              <DevPlatformCard
                name="GitLab"
                description="GitLab is a DevSecOps platform that unifies source code management, CI/CD and security testing. Enabling collaboration and automation of the full software lifecycle."
                to="/docs/default/component/traton-gitlab"
                external={false}
              />
              <DevPlatformCard
                name="Grafana"
                description="Grafana observability platform unifies metrics, logs, traces and dashboards. Hosting separate instances at EU, China and US to ensure data separation, security, compliance and scalable monitoring."
                to="https://confluence.collaborationlayer-traton.com/spaces/COLA/pages/66331960/Grafana"
                external
              />
              <DevPlatformCard
                name="Toocan Cloud"
                description="TOOCAN is an opinionated cloud platform enabling Traton DevOps teams to adopt AWS faster through standardized CDK constructs, CI/CD modules, and proven patterns for containerized workloads."
                to="/docs/default/component/toocan-golden-path"
                external={false}
              />
              <DevPlatformCard
                name="Traton Developer Portal"
                description="The Traton Developer Portal is the central hub for software development within Traton. Find and share documentation, tools, and best practices that accelerate collaboration and innovation."
                to="/docs/default/component/traton-developer-portal-docs"
                external={false}
              />
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
